import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { auth, supabase } from '../lib/supabase';
import { checkIfUserBanned, handleBannedUser } from '../utils/bannedUserCheck';
import { isFreshAccount } from '../lib/freshAccount';
import { markSignupStepDone, processReferralCode, sendSignupAlimtalk } from '../lib/signupFollowUp';

interface AuthState {
    user: SupabaseUser | null;
    isLoading: boolean;
    isInitialized: boolean;
    error: string | null;
}

interface AuthActions {
    // 상태 관리
    setUser: (user: SupabaseUser | null) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    clearError: () => void;

    // 전화번호 인증 액션
    sendOtp: (phone: string, isSignUp?: boolean) => Promise<void>;
    verifyOtp: (phone: string, token: string) => Promise<{ user: SupabaseUser }>;
    signOut: () => Promise<void>;

    // 초기화
    initialize: () => Promise<void>;
}

// 전화번호 인증 에러 메시지 변환 (참고 코드 기반 확장)
function getErrorMessage(error: any): string {
    if (typeof error === 'string') return error;

    if (error.message) {
        const message = error.message.toLowerCase();
        
        // 데이터베이스 관련 에러들 (새로 추가)
        if (message.includes('database error saving new user')) {
            return '사용자 생성 중 데이터베이스 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        }
        if (message.includes('duplicate key value') || message.includes('already exists')) {
            return '이미 가입된 전화번호입니다. 로그인을 시도해주세요.';
        }
        if (message.includes('violates not-null constraint')) {
            return '필수 정보가 누락되었습니다. 다시 시도해주세요.';
        }
        if (message.includes('permission denied') || message.includes('rls')) {
            return '권한 오류가 발생했습니다. 관리자에게 문의해주세요.';
        }
        
        // Supabase Auth 공통 에러들
        if (message.includes('phone auth provider not enabled')) {
            return 'SMS 인증 기능이 비활성화되어 있습니다. 관리자에게 문의해주세요.';
        }
        if (message.includes('phone provider not configured')) {
            return 'SMS 제공자 설정이 완료되지 않았습니다. 관리자에게 문의해주세요.';
        }
        if (message.includes('sms provider not configured')) {
            return 'SMS 서비스가 설정되지 않았습니다. 관리자에게 문의해주세요.';
        }
        if (message.includes('invalid phone number')) {
            return '올바른 전화번호를 입력해주세요. (예: 010-1234-5678)';
        }
        if (message.includes('phone number format')) {
            return '전화번호 형식이 올바르지 않습니다. 한국 휴대폰 번호를 입력해주세요.';
        }
        if (message.includes('sms sending failed') || message.includes('failed to send sms')) {
            return 'SMS 전송에 실패했습니다. 전화번호를 확인하고 다시 시도해주세요.';
        }
        if (message.includes('invalid otp') || message.includes('invalid verification code')) {
            return '인증번호가 올바르지 않습니다. 다시 확인해주세요.';
        }
        if (message.includes('otp expired') || message.includes('expired')) {
            return '인증번호가 만료되었습니다. 새로운 인증번호를 요청해주세요.';
        }
        if (message.includes('sms rate limit exceeded') || message.includes('over_sms_send_rate_limit')) {
            return 'SMS 발송 한도가 초과되었습니다. 10분 후 다시 시도해주세요.';
        }
        if (message.includes('too many requests') || message.includes('rate limit')) {
            return '너무 많은 요청이 발생했습니다. 1분 후 다시 시도해주세요.';
        }
        if (message.includes('signup disabled')) {
            return '현재 회원가입이 일시적으로 중단되었습니다.';
        }
        if (message.includes('signup blocked')) {
            return '이 전화번호는 가입이 제한되어 있습니다.';
        }
        if (message.includes('token has expired')) {
            return '인증번호가 만료되었습니다. 새로운 인증번호를 요청해주세요.';
        }
        if (message.includes('invalid token')) {
            return '인증번호가 올바르지 않습니다.';
        }
        if (message.includes('network') || message.includes('connection')) {
            return '네트워크 연결을 확인하고 다시 시도해주세요.';
        }
        if (message.includes('timeout')) {
            return '요청 시간이 초과되었습니다. 다시 시도해주세요.';
        }
        
        // 원본 에러 메시지 반환 (개발 시 디버깅용)
        return error.message;
    }

    return '알 수 없는 오류가 발생했습니다.';
}

export const useAuthStore = create<AuthState & AuthActions>()(
    persist(
        (set, get) => ({
            // 초기 상태
            user: null,
            isLoading: false,
            isInitialized: false,
            error: null,

            // 상태 관리
            setUser: (user) => set({ user }),
            setLoading: (isLoading) => set({ isLoading }),
            setError: (error) => set({ error }),
            clearError: () => set({ error: null }),

            // OTP 전송 (로그인/회원가입) - 참고 코드 로직 적용
            sendOtp: async (phone: string, isSignUp: boolean = false) => {
                set({ isLoading: true, error: null });

                try {
                    // 참고 코드와 동일하게 signInWithPhone만 사용 (회원가입/로그인 구분 없음)
                    await auth.signInWithPhone(phone);
                    
                    // OTP 전송 성공
                    console.log('✅ OTP 전송 완료:', phone);
                } catch (error: any) {
                    console.error('❌ OTP 전송 실패:', error);
                    const errorMessage = getErrorMessage(error);
                    set({ error: errorMessage });
                    throw error;
                } finally {
                    set({ isLoading: false });
                }
            },

            // OTP 검증
            verifyOtp: async (phone: string, token: string) => {
                set({ isLoading: true, error: null });

                try {
                    const user = await auth.verifyPhoneOtp(phone, token);
                    set({ user });
                    console.log('✅ 전화번호 인증 성공:', user);
                    console.log('📱 전화번호 확인:', phone);
                    
                    // 신규 가입인 경우 리퍼럴 코드 처리 + 회원가입 알림톡 발송(솔라피) — 계정은 인증번호를 '보낼 때' 생기므로 서버 가드와 같은 '생성 30분 안'으로 본다
                    // (10/22 실측 7일: 1분 안에 인증한 새 회원은 85.4% 연결, 1분 넘게 걸린 536명은 1명만 연결·알림톡 0. 서버 함수는 이미 연결된 회원이면 그대로 성공 처리)
                    // 두 번 가지 않게 하는 것은 서버(zapier-webhook + signup_alimtalk_sends)가 맡는다. 알림톡 함수는 추천 관계를 읽지 않아 둘을 함께 보낸다(10/22 —
                    // 로그인 화면이 이 뒤처리를 기다린 뒤 넘어가므로 덜 기다리게). 끝난 단계는 쪽지에 표시 — 새로고침으로 끊기면 앱이 다시 켜질 때 마저 한다(lib/signupFollowUp).
                    if (isFreshAccount(user.created_at)) {
                        console.log('🎯 신규 가입 감지 - 리퍼럴 처리·회원가입 알림톡 발송 시작');
                        await Promise.all([
                            processReferralCode(user.id).then(() => markSignupStepDone('referral')),
                            sendSignupAlimtalk(user, phone).then(() => markSignupStepDone('alimtalk')),
                        ]);
                    }
                    
                    // 전화번호 형식 변환 (데이터베이스 형식에 맞게)
                    let formattedPhone = phone.replace(/\s|-/g, "");
                    if (formattedPhone.startsWith("0")) {
                        formattedPhone = "82" + formattedPhone.substring(1);
                    } else if (formattedPhone.startsWith("+")) {
                        formattedPhone = formattedPhone.substring(1);
                    }
                    console.log('📱 차단 확인용 전화번호:', formattedPhone);
                    
                    // 차단된 사용자인지 확인
                    const isBanned = await checkIfUserBanned(formattedPhone, null);
                    console.log('🚫 차단 여부:', isBanned);
                    
                    if (isBanned) {
                        console.log('⛔ 차단된 사용자 감지! 5초 후 로그아웃 처리');
                        await handleBannedUser(user.id);
                        throw new Error('User is banned');
                    }
                    
                    return { user }; // user 반환
                } catch (error: any) {
                    console.error('❌ OTP 검증 실패:', error);
                    const errorMessage = getErrorMessage(error);
                    set({ error: errorMessage });
                    throw error;
                } finally {
                    set({ isLoading: false });
                }
            },

            // 로그아웃 - 🔥 Instagram 캐시 정리 통합
            signOut: async () => {
                set({ isLoading: true, error: null });

                try {
                    // 🔥 현재 사용자 ID 저장 (캐시 정리용)
                    const currentUserId = get().user?.id;
                    
                    // 1. Supabase 로그아웃
                    await auth.signOut();
                    
                    // 2. Auth 상태 초기화
                    set({ user: null });
                    
                    // 3. 🔥 Instagram 캐시 완전 정리
                    if (currentUserId) {
                        console.log('🧹 로그아웃 시 Instagram 캐시 정리 시작...');
                        try {
                            // Instagram Store 리셋
                            const { useInstagramStore } = await import('./useInstagramStore');
                            useInstagramStore.getState().reset();
                            
                            // 캐시 매니저로 완전 정리
                            const { clearUserInstagramData } = await import('../utils/instagramCacheManager');
                            await clearUserInstagramData(currentUserId);
                            
                            console.log('✅ Instagram 캐시 정리 완료');
                        } catch (cleanupError) {
                            console.warn('⚠️ Instagram 캐시 정리 중 경고:', cleanupError);
                            // 캐시 정리 실패는 치명적이지 않으므로 계속 진행
                        }
                    }
                    
                    // 4. 🔥 모든 로컬 스토리지 정리 (안전 조치)
                    try {
                        // last-instagram-user 키도 제거
                        localStorage.removeItem('last-instagram-user');
                        
                        // Instagram 관련 세션 스토리지도 정리
                        sessionStorage.clear();
                        
                        console.log('✅ 로컬 스토리지 정리 완료');
                    } catch (storageError) {
                        console.warn('⚠️ 스토리지 정리 중 경고:', storageError);
                    }
                    
                    console.log('🎉 로그아웃 및 모든 캐시 정리 완료!');
                    
                } catch (error: any) {
                    const errorMessage = getErrorMessage(error);
                    set({ error: errorMessage });
                    throw error;
                } finally {
                    set({ isLoading: false });
                }
            },

            // 초기화 - 세션 유효성 검증 강화
            initialize: async () => {
                set({ isLoading: true });

                try {
                    console.log('🔐 인증 초기화 시작 - 세션 유효성 검증');

                    // 1. 먼저 Supabase 세션 유효성 확인
                    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

                    // 세션이 없거나 에러가 있는 경우 — 토큰을 통째로 지우지 않는다.
                    // 만료 액세스토큰 + 네트워크 단절이면 refresh가 실패해 세션이 잠시 안 잡힐 수 있는데,
                    // 여기서 리프레시 토큰까지 지우면 네트워크 복구 후에도 영구 로그아웃이 된다.
                    // → 이번엔 로그인 화면만 표시(user=null)하고 토큰은 보존, 진짜 무효 토큰은
                    //   이후 getCurrentUser/API 호출이 401을 받아 정리한다.
                    if (sessionError || !sessionData?.session) {
                        console.warn('⚠️ 유효한 세션 없음 - 로그인 필요 (토큰 보존)');
                        set({ user: null, isLoading: false, isInitialized: true });
                        return;
                    }

                    // 2. 세션이 유효하면 사용자 정보 가져오기
                    const user = await auth.getCurrentUser();

                    if (user) {
                        console.log('✅ 인증 초기화 완료 - 사용자:', user.id);
                        set({ user });
                    } else {
                        console.warn('⚠️ 세션은 있지만 사용자 정보 없음 - 캐시 정리');
                        localStorage.removeItem('auth-storage');
                        localStorage.removeItem('instagram-store');
                        set({ user: null });
                    }
                } catch (error: any) {
                    console.error('❌ 인증 초기화 실패:', error);
                    // 네트워크 예외로 초기화가 실패해도 토큰은 보존 (다음 로드에 자동 복구).
                    // 로그아웃은 명시적 signOut 또는 확정된 무효 토큰(getCurrentUser)만 담당.
                    set({ user: null });
                } finally {
                    set({ isLoading: false, isInitialized: true });
                }
            }
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user
            })
        }
    )
);

// 인증 상태 초기화 및 변화 감지
export const initializeAuth = () => {
    const { initialize, setUser } = useAuthStore.getState();

    // 앱 시작 시 초기화
    initialize();

    // 인증 상태 변화 감지
    auth.onAuthStateChange((user) => {
        setUser(user);
    });
}; 